import type {
  ApiResponse,
  ConsumptionEvent,
  CreateConsumptionEventRequest,
  CreateDrinkEventRequest,
  CreateGroupRequest,
  CreateInstallationRequest,
  CreateParticipantRequest,
  CreateFeatureRequest,
  DeleteResourceResult,
  DrinkEvent,
  GroupAlcoholSnapshot,
  GroupCreationResult,
  GroupInvitation,
  GroupLookupResult,
  GroupMembership,
  GroupPhoto,
  ActivityFeedResult,
  InstallationBootstrap,
  InstallationMembershipBootstrap,
  JoinMembershipRequest,
  ParticipantSessionResult,
  SelectParticipantRequest,
  SessionBootstrap,
  PersonProfile,
  PushSubscriptionRequest,
  PushTestResult,
  PhotoLikeResult,
  PhotoStorageUsage,
  PhotoUploadIntent,
  PhotoUploadStatus,
  FeatureRequestResult,
  UpdateDrinkEventRequest,
  UpdateProfileRequest,
} from '@lbc/shared';

import { identityStore, installationStore } from '../persistence/identity-store';
import { resolveDebugApiOrigin } from '../../features/debug/debug-fixture-import-availability';

export const API_ORIGIN = (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/+$/, '');
export { resolveDebugApiOrigin };

export class ApiClientError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

interface RequestOptions {
  version?: 'v1' | 'v2';
  auth?: 'legacy' | 'installation';
  token?: string;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  options: RequestOptions = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (options.auth !== undefined) {
    const token = options.token ?? tokenFor(options.auth);
    if (token === null) {
      throw new ApiClientError('Keine lokale Gerätesitzung vorhanden.', 'MISSING_SESSION', 401);
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  let response: Response;
  try {
    response = await fetch(`${API_ORIGIN}/api/${options.version ?? 'v1'}${path}`, {
      ...init,
      headers,
    });
  } catch {
    throw new ApiClientError(
      'Der Server ist gerade nicht erreichbar. Bitte prüfe kurz deine Verbindung und versuche es erneut.',
      'NETWORK_ERROR',
      0,
    );
  }

  const responseBody = await response.text();
  let payload: ApiResponse<T> | null = null;
  try {
    payload = JSON.parse(responseBody) as ApiResponse<T>;
  } catch {
    // Cloudflare resource-limit pages are HTML, while the application API uses JSON.
  }
  const cloudflareCode = detectCloudflareErrorCode(responseBody);
  if (!response.ok || payload === null || payload.error !== null) {
    throw new ApiClientError(
      payload?.error?.message ?? humanReadableServerError(cloudflareCode, response.status),
      payload?.error?.code ?? cloudflareCode ?? `HTTP_${response.status}`,
      response.status,
      payload?.meta?.requestId ?? response.headers.get('CF-Ray') ?? undefined,
    );
  }
  return payload.data;
}

async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const headers = new Headers();
  if (options.auth !== undefined) {
    const token = options.token ?? tokenFor(options.auth);
    if (token === null) {
      throw new ApiClientError('Keine lokale Gerätesitzung vorhanden.', 'MISSING_SESSION', 401);
    }
    headers.set('Authorization', `Bearer ${token}`);
  }
  let response: Response;
  try {
    response = await fetch(`${API_ORIGIN}/api/${options.version ?? 'v1'}${path}`, { headers });
  } catch {
    throw new ApiClientError(
      'Der Server ist gerade nicht erreichbar. Bitte prüfe kurz deine Verbindung und versuche es erneut.',
      'NETWORK_ERROR',
      0,
    );
  }
  if (!response.ok) {
    const responseBody = await response.text();
    let payload: ApiResponse<unknown> | null = null;
    try {
      payload = JSON.parse(responseBody) as ApiResponse<unknown>;
    } catch {
      // Cloudflare resource-limit pages are HTML.
    }
    const cloudflareCode = detectCloudflareErrorCode(responseBody);
    throw new ApiClientError(
      payload?.error?.message ?? humanReadableServerError(cloudflareCode, response.status),
      payload?.error?.code ?? cloudflareCode ?? `HTTP_${response.status}`,
      response.status,
      payload?.meta?.requestId ?? response.headers.get('CF-Ray') ?? undefined,
    );
  }
  return response.blob();
}

function detectCloudflareErrorCode(responseBody: string): string | null {
  const match = responseBody.match(/(?:error\s*)?(1027|1082|1102)\b/i);
  return match?.[1] ?? null;
}

function humanReadableServerError(cloudflareCode: string | null, status: number): string {
  if (cloudflareCode === '1027') {
    return 'Der tägliche Anfrage-Rahmen des Servers ist gerade erreicht. Bitte versuche es später erneut; er wird automatisch zurückgesetzt.';
  }
  if (cloudflareCode === '1082' || cloudflareCode === '1102') {
    return 'Der Server ist gerade stark ausgelastet. Bitte warte kurz und versuche es erneut.';
  }
  if (status === 502 || status === 503 || status === 504) {
    return 'Der Server ist vorübergehend nicht verfügbar. Bitte versuche es in wenigen Sekunden erneut.';
  }
  return 'Die Anfrage konnte gerade nicht verarbeitet werden. Bitte versuche es erneut.';
}

function tokenFor(auth: NonNullable<RequestOptions['auth']>): string | null {
  if (auth === 'installation') {
    return installationStore.read()?.installationToken ?? null;
  }
  return (
    installationStore.selectedMembership()?.legacyIdentity.sessionToken ??
    identityStore.read()?.sessionToken ??
    null
  );
}

export const api = {
  createInstallation(input: CreateInstallationRequest): Promise<InstallationBootstrap> {
    return request(
      '/installations',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      { version: 'v2' },
    );
  },

  createGroupV2(input: CreateGroupRequest): Promise<GroupCreationResult> {
    return request(
      '/groups',
      { method: 'POST', body: JSON.stringify(input) },
      {
        version: 'v2',
        auth: 'installation',
      },
    );
  },

  lookupGroup(code: string): Promise<GroupLookupResult> {
    return request(
      '/group-lookup',
      { method: 'POST', body: JSON.stringify({ code }) },
      { version: 'v2' },
    );
  },

  createParticipantV2(
    groupId: string,
    input: CreateParticipantRequest,
  ): Promise<ParticipantSessionResult> {
    return request(
      `/groups/${groupId}/participants`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      { version: 'v2', auth: 'installation' },
    );
  },

  selectParticipantV2(input: SelectParticipantRequest): Promise<ParticipantSessionResult> {
    return request(
      `/groups/${input.groupId}/participants/select`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      { version: 'v2', auth: 'installation' },
    );
  },

  bootstrap(sessionToken?: string): Promise<SessionBootstrap> {
    return request(
      '/bootstrap',
      {},
      {
        auth: 'legacy',
        ...(sessionToken === undefined ? {} : { token: sessionToken }),
      },
    );
  },

  alcoholHistory(
    date: string,
    period: 'day' | 'week' = 'day',
    sessionToken?: string,
  ): Promise<GroupAlcoholSnapshot> {
    const selectedGroupId = installationStore.selectedMembership()?.groupId;
    if (sessionToken === undefined && selectedGroupId !== undefined) {
      return this.groupAlcoholHistory(selectedGroupId, date, period);
    }
    return request(
      `/alcohol-history?date=${encodeURIComponent(date)}&period=${period}`,
      {},
      {
        auth: 'legacy',
        ...(sessionToken === undefined ? {} : { token: sessionToken }),
      },
    );
  },

  createDrinkEvent(event: CreateDrinkEventRequest): Promise<DrinkEvent> {
    return request(
      '/drink-events',
      {
        method: 'POST',
        body: JSON.stringify(event),
        headers: { 'Idempotency-Key': event.id },
      },
      { auth: 'legacy' },
    );
  },

  updateDrinkEvent(eventId: string, input: UpdateDrinkEventRequest): Promise<DrinkEvent> {
    return request(
      `/drink-events/${eventId}`,
      { method: 'PATCH', body: JSON.stringify(input) },
      { auth: 'legacy' },
    );
  },

  deleteDrinkEvent(eventId: string): Promise<DrinkEvent> {
    return request(`/drink-events/${eventId}`, { method: 'DELETE' }, { auth: 'legacy' });
  },

  createInvitation(groupId: string): Promise<GroupInvitation> {
    return request(
      `/groups/${groupId}/invitations`,
      { method: 'POST' },
      {
        version: 'v2',
        auth: 'installation',
      },
    );
  },

  revokeCurrentSession(): Promise<DeleteResourceResult> {
    return request('/sessions/current', { method: 'DELETE' }, { auth: 'legacy' });
  },

  // Kept only for a rollback-capable v1 client. The v2 UI never exposes these actions.
  deleteParticipant(participantId: string): Promise<DeleteResourceResult> {
    return request(`/participants/${participantId}`, { method: 'DELETE' }, { auth: 'legacy' });
  },

  deleteGroup(groupId: string): Promise<DeleteResourceResult> {
    return request(`/groups/${groupId}`, { method: 'DELETE' }, { auth: 'legacy' });
  },

  installationBootstrap(token?: string): Promise<InstallationMembershipBootstrap> {
    return request(
      '/installations/bootstrap',
      {},
      { version: 'v2', auth: 'installation', ...(token === undefined ? {} : { token }) },
    );
  },

  joinMembership(input: JoinMembershipRequest): Promise<GroupMembership> {
    return request(
      '/memberships',
      { method: 'POST', body: JSON.stringify(input) },
      { version: 'v2', auth: 'installation' },
    );
  },

  leaveMembership(membershipId: string): Promise<GroupMembership> {
    return request(
      `/memberships/${membershipId}`,
      { method: 'DELETE' },
      {
        version: 'v2',
        auth: 'installation',
      },
    );
  },

  groupBootstrap(groupId: string, extendedForecast = false): Promise<SessionBootstrap> {
    const forecastQuery = extendedForecast ? '?forecast=1' : '';
    return request(
      `/groups/${groupId}/bootstrap${forecastQuery}`,
      {},
      {
        version: 'v2',
        auth: 'installation',
      },
    );
  },

  activityFeed(from: string, to: string, since?: string): Promise<ActivityFeedResult> {
    const params = new URLSearchParams({ from, to });
    if (since !== undefined) params.set('since', since);
    return request(
      `/activity-feed?${params.toString()}`,
      {},
      { version: 'v2', auth: 'installation' },
    );
  },

  wrappedGroupBootstrap(groupId: string): Promise<SessionBootstrap> {
    return request(
      `/groups/${groupId}/bootstrap?wrapped=1`,
      {},
      {
        version: 'v2',
        auth: 'installation',
      },
    );
  },

  pushPublicKey(): Promise<{ publicKey: string }> {
    return request('/push/public-key', {}, { version: 'v2' });
  },

  updateProfile(input: UpdateProfileRequest): Promise<PersonProfile> {
    return request(
      '/profile',
      { method: 'PATCH', body: JSON.stringify(input) },
      { version: 'v2', auth: 'installation' },
    );
  },

  updateMembershipName(membershipId: string, displayName: string): Promise<GroupMembership> {
    return request(
      `/memberships/${membershipId}`,
      { method: 'PATCH', body: JSON.stringify({ displayName }) },
      { version: 'v2', auth: 'installation' },
    );
  },

  sendFeatureRequest(
    groupId: string,
    input: CreateFeatureRequest & { images?: readonly File[] },
  ): Promise<FeatureRequestResult> {
    const form = new FormData();
    form.set('id', input.id);
    form.set('problem', input.problem);
    form.set('description', input.description);
    for (const image of input.images ?? []) form.append('image', image);
    return request(
      `/groups/${groupId}/feature-requests`,
      {
        method: 'POST',
        body: form,
        headers: { 'Idempotency-Key': input.id },
      },
      { version: 'v2', auth: 'installation' },
    );
  },

  savePushSubscription(input: PushSubscriptionRequest): Promise<void> {
    return request(
      '/push-subscriptions',
      { method: 'POST', body: JSON.stringify(input) },
      { version: 'v2', auth: 'installation' },
    );
  },

  removePushSubscription(endpoint: string): Promise<void> {
    return request(
      '/push-subscriptions',
      { method: 'DELETE', body: JSON.stringify({ endpoint }) },
      { version: 'v2', auth: 'installation' },
    );
  },

  pushTest(): Promise<PushTestResult> {
    return request('/push-test', { method: 'POST' }, { version: 'v2', auth: 'installation' });
  },

  groupAlcoholHistory(
    groupId: string,
    date: string,
    period: 'day' | 'week' = 'day',
  ): Promise<GroupAlcoholSnapshot> {
    return request(
      `/groups/${groupId}/alcohol-history?date=${encodeURIComponent(date)}&period=${period}`,
      {},
      { version: 'v2', auth: 'installation' },
    );
  },

  createConsumption(event: CreateConsumptionEventRequest): Promise<ConsumptionEvent> {
    return request(
      '/consumptions',
      {
        method: 'POST',
        body: JSON.stringify(event),
        headers: { 'Idempotency-Key': event.id },
      },
      { version: 'v2', auth: 'installation' },
    );
  },

  consumptions(): Promise<ConsumptionEvent[]> {
    return request('/consumptions', {}, { version: 'v2', auth: 'installation' });
  },

  groupPhotos(groupId: string, from?: string, to?: string): Promise<GroupPhoto[]> {
    const query = new URLSearchParams();
    if (from !== undefined) query.set('from', from);
    if (to !== undefined) query.set('to', to);
    const suffix = query.toString() === '' ? '' : `?${query.toString()}`;
    return request(
      `/groups/${groupId}/photos${suffix}`,
      {},
      { version: 'v2', auth: 'installation' },
    );
  },

  groupPhotoHighlights(groupId: string): Promise<GroupPhoto[]> {
    return request(
      `/groups/${groupId}/photos/highlights`,
      {},
      { version: 'v2', auth: 'installation' },
    );
  },

  photo(photoId: string): Promise<GroupPhoto> {
    return request(`/photos/${photoId}`, {}, { version: 'v2', auth: 'installation' });
  },

  photoContent(photoId: string): Promise<Blob> {
    return requestBlob(`/photos/${photoId}/download`, { version: 'v2', auth: 'installation' });
  },

  photoStorageUsage(groupId: string): Promise<PhotoStorageUsage> {
    return request(
      `/groups/${groupId}/photos/storage-usage`,
      {},
      { version: 'v2', auth: 'installation' },
    );
  },

  photoUploadStatus(groupId: string): Promise<PhotoUploadStatus> {
    return request(
      `/groups/${groupId}/photos/upload-status`,
      {},
      { version: 'v2', auth: 'installation' },
    );
  },

  togglePhotoLike(photoId: string, groupId: string): Promise<PhotoLikeResult> {
    return request(
      `/photos/${photoId}/like`,
      { method: 'POST', body: JSON.stringify({ groupId }) },
      { version: 'v2', auth: 'installation' },
    );
  },

  async uploadPhoto(
    groupId: string,
    drinkEventId: string,
    input: {
      blob: Blob;
      mimeType: 'image/jpeg' | 'image/png' | 'image/webp';
      width: number;
      height: number;
    },
  ): Promise<GroupPhoto> {
    const intent = await request<PhotoUploadIntent>(
      `/groups/${groupId}/photos/intents`,
      {
        method: 'POST',
        body: JSON.stringify({
          drinkEventId,
          byteSize: input.blob.size,
          width: input.width,
          height: input.height,
          mimeType: input.mimeType,
        }),
      },
      { version: 'v2', auth: 'installation' },
    );
    let uploadResponse: Response;
    try {
      const installationToken = installationStore.read()?.installationToken ?? null;
      if (installationToken === null) {
        throw new ApiClientError('Keine lokale Gerätesitzung vorhanden.', 'MISSING_SESSION', 401);
      }
      const uploadHeaders = new Headers(intent.uploadHeaders);
      uploadHeaders.set('Authorization', `Bearer ${installationToken}`);
      uploadResponse = await fetch(intent.uploadUrl, {
        method: 'PUT',
        headers: uploadHeaders,
        body: input.blob,
      });
    } catch {
      throw new ApiClientError(
        'Das Foto konnte nicht hochgeladen werden. Bitte prüfe kurz deine Verbindung.',
        'PHOTO_UPLOAD_NETWORK_ERROR',
        0,
      );
    }
    if (!uploadResponse.ok) {
      throw new ApiClientError(
        'Das Foto konnte nicht hochgeladen werden. Bitte versuche es erneut.',
        'PHOTO_UPLOAD_FAILED',
        uploadResponse.status,
      );
    }
    return request(
      `/photos/${intent.photoId}/complete`,
      { method: 'POST' },
      { version: 'v2', auth: 'installation' },
    );
  },

  updateConsumption(eventId: string, input: UpdateDrinkEventRequest): Promise<ConsumptionEvent> {
    return request(
      `/consumptions/${eventId}`,
      { method: 'PATCH', body: JSON.stringify(input) },
      { version: 'v2', auth: 'installation' },
    );
  },

  deleteConsumption(eventId: string): Promise<ConsumptionEvent> {
    return request(
      `/consumptions/${eventId}`,
      { method: 'DELETE' },
      {
        version: 'v2',
        auth: 'installation',
      },
    );
  },
};
